import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Baby, Calendar, User, X } from 'lucide-react';
import { calculateAge } from '../utils/dateUtils';
import { format } from 'date-fns';

export const ChildSelector = ({ 
  children, 
  selectedChild, 
  onSelectChild, 
  onAddChild, 
  onEditChild, 
  onDeleteChild,
  loading = false,
  showAddForm = false,
  setShowAddForm = () => {}
}) => {
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    birthDate: '',
    profileImage: null,
    theme: 'moon-stars'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (showEditForm && editingChild) {
      setFormData({
        name: editingChild.name || '',
        birthDate: editingChild.birthDate ? editingChild.birthDate.split('T')[0] : '',
        profileImage: editingChild.profileImage || null,
        theme: editingChild.theme || 'moon-stars'
      });
    } else if (showAddForm) {
      setFormData({
        name: '',
        birthDate: '',
        profileImage: null,
        theme: 'moon-stars'
      });
    }
    setErrors({});
  }, [showAddForm, showEditForm, editingChild]);

  // Listen for FAB clicks
  useEffect(() => {
    const handleOpenAddForm = () => {
      setShowAddForm(true);
    };

    window.addEventListener('openAddChildForm', handleOpenAddForm);
    return () => {
      window.removeEventListener('openAddChildForm', handleOpenAddForm);
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.birthDate) {
      newErrors.birthDate = 'Geburtsdatum ist erforderlich';
    } else {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      if (birthDate > today) {
        newErrors.birthDate = 'Geburtsdatum kann nicht in der Zukunft liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      if (showEditForm && editingChild) {
        await onEditChild(editingChild.id, formData);
        setShowEditForm(false);
        setEditingChild(null);
      } else {
        await onAddChild(formData);
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Error saving child:', error);
    }
  };

  const handleEdit = (child) => {
    setEditingChild(child);
    setShowEditForm(true);
  };

  const handleDelete = async (child) => {
    if (window.confirm(`Möchtest du "${child.name}" wirklich löschen? Alle zugehörigen Daten werden ebenfalls gelöscht.`)) {
      try {
        await onDeleteChild(child.id);
      } catch (error) {
        console.error('Error deleting child:', error);
      }
    }
  };

  const getAgeDisplay = (birthDate) => {
    if (!birthDate) return null;
    const age = calculateAge(birthDate);
    return age.display;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const closeModal = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingChild(null);
    setErrors({});
  };

  return (
    <div className="space-y-4">
      <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Kinder verwalten</h2>
        <p className="text-gray-600 dark:text-gray-400">Verwalte die Profile deiner Kinder</p>
      </div>

      {loading ? (
        <div className="card text-center py-8">
          <div className="mb-4">
            <div className="spinner mx-auto"></div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Lade Kinder...
          </h3>
        </div>
      ) : (
        children.length === 0 ? (
          <div className="card text-center py-8">
            <div className="mb-4">
              <Baby className="w-16 h-16 mx-auto text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Keine Kinder hinzugefügt
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Füge dein erstes Kind hinzu, um mit dem Schlaf-Tracking zu beginnen.
            </p>
            <button 
              className="btn-primary w-full flex items-center justify-center gap-2"
              onClick={() => setShowAddForm(true)}
              data-add-child
            >
              <Plus className="w-5 h-5" />
              Erstes Kind hinzufügen
            </button>
          </div>
        ) : (
          <>
            {children.map(child => (
              <div className="card hover:shadow-lg transition-shadow" key={child.id}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                      {child.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                        {child.name}
                      </h4>
                      {child.birthDate && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(child.birthDate), 'dd.MM.yyyy')}
                          {getAgeDisplay(child.birthDate) && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              {getAgeDisplay(child.birthDate)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onSelectChild(child)}
                      className="btn-primary px-4 py-2 text-sm dark:bg-blue-800 dark:hover:bg-blue-900 dark:text-white"
                    >
                      Auswählen
                    </button>
                    <button
                      onClick={() => handleEdit(child)}
                      className="btn-secondary p-2 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                      aria-label="Bearbeiten"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(child)}
                      className="btn-secondary p-2 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                      aria-label="Löschen"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )
      )}

      {/* Floating Action Button for adding children */}
      {children.length > 0 && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fab"
          aria-label="Neues Kind hinzufügen"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Add/Edit Modal */}
      {(showAddForm || showEditForm) && (
        <div className="modal">
          <div className="modal-content max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {showEditForm ? 'Kind bearbeiten' : 'Neues Kind hinzufügen'}
              </h3>
              <button
                onClick={closeModal}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Name des Kindes"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Geburtsdatum *
                </label>
                <input
                  id="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={(e) => handleInputChange('birthDate', e.target.value)}
                  className={errors.birthDate ? 'border-red-500' : ''}
                />
                {errors.birthDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.birthDate}</p>
                )}
              </div>

              <div>
                <label htmlFor="theme" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Theme
                </label>
                <select
                  id="theme"
                  value={formData.theme}
                  onChange={(e) => handleInputChange('theme', e.target.value)}
                >
                  <option value="default">Standard</option>
                  <option value="moon-stars">Mond & Sterne</option>
                  <option value="sunset">Sonnenuntergang</option>
                  <option value="cloudy">Wolken</option>
                  <option value="hearts">Herzen</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {showEditForm ? 'Aktualisieren' : 'Hinzufügen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}; 